import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { ActionDetail, ContactPickItem } from '@tww/shared';
import { fmtDate, fmtEur } from '@tww/shared';
import { api, ApiRequestError } from '../../api/client';
import { useLookup } from '../../api/hooks';
import { ContactPicker } from '../../ui/ContactPicker';
import { ErrorState, Skeleton } from '../../ui/basics';
import { DateInput, NumberInput, Select, TextArea, TextInput } from '../../ui/fields';
import { useToast } from '../../ui/Toast';

interface F {
  category_code: string;
  amount: number | null;
  invoice_no: string;
  invoice_date: string;
  note: string;
}

/** Rechnung einer Aktion erfassen oder bearbeiten (Tab „Kosten“): eigene Seite statt Modal */
export function ActionCostFormPage() {
  const { id, costId } = useParams();
  const aid = Number(id);
  const cid = costId && costId !== 'neu' ? Number(costId) : null;
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const cats = useLookup('cost_categories');
  const q = useQuery({ queryKey: ['action', aid], queryFn: () => api.get<ActionDetail>(`/actions/${aid}`) });
  const [f, setF] = useState<F | null>(null);
  const [supplier, setSupplier] = useState<ContactPickItem | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const back = `/aktionen/${aid}?tab=kosten`;
  useEffect(() => {
    if (!q.data || f) return;
    const c = cid ? q.data.costs.find((x) => x.id === cid) : null;
    setF({ category_code: c?.category_code ?? '', amount: c?.amount ?? null, invoice_no: c?.invoice_no ?? '', invoice_date: c?.invoice_date ?? '', note: c?.note ?? '' });
    if (c?.supplier) setSupplier({ id: c.supplier.id, display_name: c.supplier.display_name, sub: null, rating_grade: null, rating_reason: null, city: null, region: null, last_price: null, categories: [] });
  }, [q.data, f, cid]);
  const save = useMutation({
    mutationFn: () => {
      const body = { category_code: f!.category_code, amount: f!.amount ?? 0, invoice_no: f!.invoice_no.trim() || null, invoice_date: f!.invoice_date || null, supplier_contact_id: supplier?.id ?? null, note: f!.note.trim() || null };
      return cid ? api.patch(`/actions/${aid}/costs/${cid}`, body) : api.post(`/actions/${aid}/costs`, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['action', aid] });
      toast('Rechnung gespeichert');
      nav(back);
    },
    onError: (e: Error) => {
      if (e instanceof ApiRequestError && e.fields) setErrors(e.fields);
      toast(e.message, 'error');
    },
  });
  if (q.isLoading || (q.data && !f)) return <Skeleton rows={6} />;
  if (q.isError || !q.data) return <ErrorState error={q.error ?? new Error('Aktion nicht gefunden')} retry={() => q.refetch()} />;
  const a = q.data;
  if (cid && !a.costs.some((x) => x.id === cid)) return <ErrorState error={new Error('Rechnung nicht gefunden')} />;
  const form = f!;
  const set = (p: Partial<F>) => setF((x) => ({ ...x!, ...p }));
  const missing = [!form.category_code && 'Kategorie', !form.amount && 'Betrag'].filter(Boolean) as string[];
  return (
    <>
      <div className="crumb">
        <Link to="/aktionen">Aktionen</Link> › <Link to={back}>{a.label}</Link>
      </div>
      <div className="head">
        <div>
          <h1>{cid ? 'Rechnung bearbeiten' : 'Rechnung erfassen'}</h1>
          <div className="sub">{a.code}</div>
        </div>
      </div>
      <div className="page-form">
        <div className="section">
          <div className="sh">
            <h2>Rechnung</h2>
          </div>
          <div className="sb">
            <div className="form">
              <Select label="Kategorie" required value={form.category_code} onChange={(v) => set({ category_code: v })} options={cats.values.map((c) => ({ value: c.code, label: c.label }))} placeholder="wählen" error={errors.category_code} />
              <NumberInput label="Betrag brutto (€)" required value={form.amount} onChange={(v) => set({ amount: v })} decimals={2} error={errors.amount} />
              <TextInput label="Rechnungsnummer" value={form.invoice_no} onChange={(v) => set({ invoice_no: v })} error={errors.invoice_no} />
              <DateInput label="Rechnungsdatum" value={form.invoice_date} onChange={(v) => set({ invoice_date: v })} error={errors.invoice_date} />
              <ContactPicker label="Lieferant" value={supplier} onChange={setSupplier} category="supplier" error={errors.supplier_contact_id} />
              <TextArea label="Notiz" value={form.note} onChange={(v) => set({ note: v })} rows={2} error={errors.note} />
            </div>
          </div>
        </div>
        <div className="aside">
          <div className="section">
            <div className="sb summary-box">
              <strong>{a.label}</strong>
              <dl>
                <dt>Kategorie</dt>
                <dd>{cats.values.find((c) => c.code === form.category_code)?.label ?? '–'}</dd>
                <dt>Betrag</dt>
                <dd>{form.amount ? fmtEur(form.amount) : '–'}</dd>
                <dt>Rechnungsdatum</dt>
                <dd>{fmtDate(form.invoice_date || null)}</dd>
                <dt>Bisher erfasst</dt>
                <dd>{fmtEur(a.cost_total)}</dd>
              </dl>
            </div>
          </div>
          <div className="actions-col">
            <button type="button" className="btn primary" disabled={missing.length > 0 || save.isPending} onClick={() => save.mutate()}>
              Speichern
            </button>
            {missing.length > 0 && <span className="mut small">Es fehlt: {missing.join(', ')}</span>}
            <Link className="btn" to={back}>
              Abbrechen
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
